import React, { useState, useEffect, useMemo } from "react";
import { withRouter } from "../utils/withRouter.js";
import { useNavigate } from "react-router";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import Form from "react-bootstrap/Form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createClass,
  getCountryGrades,
  getSchools,
  getTeachers,
  whoami,
  updateUserActivity,
  getAvailableTransitionedClasses,
  getCountryId,
  getNextYearClassConfig,
  getNextYearClassEligibility,
} from "../services/APIClient";
import SimpleNavigationButton from "./SimpleNavigationButton";
import ConfirmClassOwnerModal from "./ConfirmClassOwnerModal";
import SeniorInfantPreCreateModal from "./SeniorInfantPreCreateModal";
import FutureYearConfirmationModal from "./FutureYearConfirmationModal";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";
import { intersectAcademicYearOptions } from "../utils/academicYearPolicy";
import {
  destinationWithSelection,
  getGradeLevelFromProgressionKey,
} from "../utils/classroomHandoverSelection";

export const isAcademicYearSubmissionAllowed = (
  configEnabled,
  selectedStorageYear,
  configuredOptions
) =>
  !configEnabled ||
  configuredOptions.some(({ storage }) => storage === selectedStorageYear);

export function CreateClassForm(props) {
  const handoverSelection = props.handoverSelection || null;
  const handoverGradeLabel = handoverSelection?.progressionPoint?.label || "";
  const handoverReturnDestination = handoverSelection
    ? destinationWithSelection({
        ...handoverSelection.progressionPoint?.destination,
        academicYear: handoverSelection.academicYear,
        progressionKey: handoverSelection.progressionKey,
      })
    : "/classadmindashboard";
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(
    handoverGradeLabel
  );
  const [classTitle, setClassTitle] = useState("");
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [classTitleError, setClassTitleError] = useState("");
  const [gradeError, setGradeError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [classOwner, setClassOwner] = useState(null);

  const [countryId, setCountryId] = useState(null);
  const [isInviteColleague, setIsInviteColleague] = useState(false);

  const [showSeniorInfantModal, setShowSeniorInfantModal] = useState(false);
  const [availableConvertedClasses, setAvailableConvertedClasses] = useState([]);

  // Academic Year state
  const [academicYearConfig, setAcademicYearConfig] = useState({
    enabled: false,
    availableYears: [],
  });
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(
    handoverSelection?.academicYear || ""
  );
  const [academicYearError, setAcademicYearError] = useState("");
  const [showFutureYearModal, setShowFutureYearModal] = useState(false);
  const creationYearOptions = useAcademicYearOptions({
    asOfDate: props.asOfDate,
    futureYears: 1,
  });
  const currentAcademicYear = creationYearOptions.find(
    ({ startYear }) => startYear === Math.min(...creationYearOptions.map((year) => year.startYear))
  );
  const configuredAcademicYears = useMemo(
    () => intersectAcademicYearOptions(
      academicYearConfig.availableYears,
      creationYearOptions
    ),
    [academicYearConfig.availableYears, creationYearOptions]
  );
  const selectedAcademicYearOption = configuredAcademicYears.find(
    ({ storage }) => storage === selectedAcademicYear
  );

  // Eligibility state
  const [eligibility, setEligibility] = useState({
    isPaidCustomer: false,
    currentOperatingYear: "",
    hasTransitioningClasses: false,
    transitionDetails: {
      grade1ToGrade2: {
        hasClasses: false,
        grade1Name: "",
        grade2Name: "",
        classCount: 0
      },
      grade2ToGrade3: {
        hasClasses: false,
        grade2Name: "",
        grade3Name: "",
        classCount: 0
      }
    }
  });

  const teacherId = props.teacherid;
  const schoolid = props.router.location.state.schoolid.schoolid;
  let teacherRoles = props.teacherRoles;

  const [rollNumber, setRollNumber] = useState("");

  const [teacherEmail, setTeacherEmail] = useState("");
  const [name, setName] = useState("");
  const navigate = useNavigate();

  let teachers = teacherOptions;
  let fromCreateClass = true;

  useEffect(() => {
    console.log(isInviteColleague);
  }, [isInviteColleague]);

  useEffect(() => {
    whoami().then((response) => {
      console.log(response, "WHOAMI");
      setTeacherEmail(response.data.email);
      setName(response.data.stakeholder.name);
    });
    getSchools().then((response) => {
      console.log(response);
      setCountryId(response.data.countryid);
      setRollNumber(response.data.rollnumber);
    });
  }, []);

  // Fetch academic year configuration when countryId is available
  useEffect(() => {
    const fetchAcademicYearConfig = async () => {
      if (countryId) {
        try {
          const config = await getNextYearClassConfig(countryId);
          console.log("Academic year config:", config);
          setAcademicYearConfig(config);
        } catch (error) {
          console.error("Error fetching academic year config:", error);
          setAcademicYearConfig({ enabled: false, availableYears: [] });
        }
      }
    };

    fetchAcademicYearConfig();
  }, [countryId]);

  // Default explicitly to the current year when it is enabled by configuration.
  useEffect(() => {
    setSelectedAcademicYear((previousSelection) => {
      if (handoverSelection?.academicYear && configuredAcademicYears.length === 0) {
        return previousSelection;
      }
      if (configuredAcademicYears.some(({ storage }) => storage === previousSelection)) {
        return previousSelection;
      }
      return configuredAcademicYears.some(
        ({ storage }) => storage === currentAcademicYear?.storage
      ) ? currentAcademicYear.storage : "";
    });
  }, [configuredAcademicYears, currentAcademicYear, handoverSelection?.academicYear]);

  // Fetch eligibility when schoolid is available
  useEffect(() => {
    const fetchEligibility = async () => {
      if (schoolid) {
        try {
          const eligibilityData = await getNextYearClassEligibility(schoolid);
          console.log("Eligibility data:", eligibilityData);
          setEligibility(eligibilityData);
        } catch (error) {
          console.error("Error fetching eligibility:", error);
        }
      }
    };

    fetchEligibility();
  }, [schoolid]);

  const defaultGradeLevel = () => {
    const lowerCasedRoles = teacherRoles.map((role) => role.toLowerCase());

    if (
      lowerCasedRoles.some((role) => role.includes("set")) ||
      lowerCasedRoles.some((role) => role.includes("senco"))
    ) {
      return "";
    }

    if (lowerCasedRoles.some((role) => role.includes("junior infant"))) {
      return "Junior Infants";
    }

    if (lowerCasedRoles.some((role) => role.includes("senior infant"))) {
      return "Senior Infants";
    }

    return "";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (schoolid) {
          const countryResponse = await getCountryId(schoolid);

          if (countryResponse.countryId) {
            console.log(countryResponse);
            setCountryId(countryResponse.countryId);

            const gradesResponse = await getCountryGrades(countryResponse.countryId);
            const { grade1, grade2, grade3 } = gradesResponse;

            let gradeOptions = [grade1, grade2];

            if (grade3) {
              gradeOptions.push(grade3);
            }

            if (handoverGradeLabel && !gradeOptions.includes(handoverGradeLabel)) {
              gradeOptions.push(handoverGradeLabel);
            }

            setGradeLevelOptions(gradeOptions);

            const teachersResponse = await getTeachers(schoolid);
            setTeacherOptions(teachersResponse);

            const defaultGrade = defaultGradeLevel();
            if (defaultGrade && !handoverGradeLabel) {
              setSelectedGradeLevel(defaultGrade);
            }
          } else {
            console.warn("No country ID found for school:", countryResponse.message);
            setCountryId(null);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setCountryId(null);
      }
    };

    fetchData();
  }, [schoolid, teacherRoles, handoverGradeLabel]);

  const fetchAvailableConvertedClasses = async () => {
    try {
      const currentYearStorage = currentAcademicYear?.storage;
      if (!currentYearStorage) return;
      const gradeLevel = 2;

      const transitionedClassesResponse = await getAvailableTransitionedClasses(
        schoolid,
        gradeLevel,
        currentYearStorage
      );

      if (!transitionedClassesResponse.success) {
        console.log('No transitioned classes available');
        setAvailableConvertedClasses([]);
        return;
      }

      const formattedClasses = transitionedClassesResponse.classes.map(classItem => ({
        name: classItem.name,
        hasOwner: false,
        studentCount: classItem.studentCount || 0,
        id: classItem.id
      }));

      setAvailableConvertedClasses(formattedClasses);

      if (formattedClasses.length > 0) {
        setShowSeniorInfantModal(true);
      }
    } catch (error) {
      console.error("Error fetching available transitioned classes:", error);
      setAvailableConvertedClasses([]);
    }
  };

  useEffect(() => {
    if (selectedGradeLevel === gradeLevelOptions[1]) {
      fetchAvailableConvertedClasses();
    }
  }, [selectedGradeLevel, gradeLevelOptions, currentAcademicYear]);

  useEffect(() => {
    if (submitted) {
      const isClassTitleValid = classTitle.length > 0;
      const isGradeValid = selectedGradeLevel.length > 0;

      setClassTitleError(isClassTitleValid ? "" : "Please enter a class name.");
      setGradeError(isGradeValid ? "" : "Please select a grade level.");
    }
  }, [classTitle, selectedGradeLevel, submitted]);

  const handleSeniorInfantModalClose = () => {
    setShowSeniorInfantModal(false);
    setSelectedGradeLevel("");
  };

  const handleSeniorInfantModalProceed = () => {
    setShowSeniorInfantModal(false);
  };

  // Helper function to determine which transition warning to show
  const getTransitionWarning = () => {
    if (!eligibility.hasTransitioningClasses) return null;
    const handoverGradeLevel = getGradeLevelFromProgressionKey(
      handoverSelection?.progressionKey
    );
    if (
      handoverSelection &&
      selectedGradeLevel === handoverGradeLabel &&
      handoverGradeLevel !== 2 &&
      handoverGradeLevel !== 3
    ) return null;

    // Check if creating grade 2 (SI) and has grade 1 (JI) classes
    if (selectedGradeLevel === gradeLevelOptions[1] &&
      eligibility.transitionDetails.grade1ToGrade2.hasClasses) {
      const transition = eligibility.transitionDetails.grade1ToGrade2;
      return {
        fromGradeName: transition.grade1Name,
        toGradeName: transition.grade2Name,
      };
    }

    // Check if creating grade 3 (1st) and has grade 2 (SI) classes
    if (selectedGradeLevel === gradeLevelOptions[2] &&
      eligibility.transitionDetails.grade2ToGrade3.hasClasses) {
      const transition = eligibility.transitionDetails.grade2ToGrade3;
      return {
        fromGradeName: transition.grade2Name,
        toGradeName: transition.grade3Name,
      };
    }

    return null;
  };

  function handleSubmit(event) {
    event.preventDefault();
    setSubmitted(true);

    const isClassTitleValid = classTitle.length > 0;
    const isGradeValid = selectedGradeLevel.length > 0;
    const isAcademicYearValid = isAcademicYearSubmissionAllowed(
      academicYearConfig.enabled,
      selectedAcademicYear,
      configuredAcademicYears
    );

    if (!isClassTitleValid || !isGradeValid || !isAcademicYearValid) {
      setClassTitleError(
        !isClassTitleValid ? "Please enter a class name." : ""
      );
      setGradeError(!isGradeValid ? "Please select a grade level." : "");
      setAcademicYearError(
        !isAcademicYearValid ? "Please select an available academic year." : ""
      );
      return;
    }

    if (
      selectedAcademicYearOption &&
      selectedAcademicYearOption.startYear > currentAcademicYear.startYear
    ) {
      setShowFutureYearModal(true);
    } else {
      // No year selected or current year - go straight to owner modal
      setShowModal(true);
    }
  }

  function handleModalConfirm(
    selectedTeacherId,
    inviteColleagueClicked = isInviteColleague
  ) {
    if (!isAcademicYearSubmissionAllowed(
      academicYearConfig.enabled,
      selectedAcademicYear,
      configuredAcademicYears
    )) {
      setAcademicYearError("The selected academic year is no longer available.");
      setShowModal(false);
      return;
    }

    const matchingGrade = gradeLevelOptions.includes(selectedGradeLevel);
    if (matchingGrade) {
      const trialClass = false;
      const setClass = false;
      const grade = selectedGradeLevel;
      const formattedClassTitle = `${classTitle}`;
      const finalClassOwner = selectedTeacherId || teacherId;

      const handoverGradeLevel = selectedGradeLevel === handoverGradeLabel
        ? getGradeLevelFromProgressionKey(handoverSelection?.progressionKey)
        : null;
      let isGrade1 = handoverGradeLevel === 1;
      let isGrade2 = handoverGradeLevel === 2;
      let isGrade3 = handoverGradeLevel === 3;

      if (handoverGradeLevel === null && grade === gradeLevelOptions[0]) {
        isGrade1 = true;
      } else if (handoverGradeLevel === null && grade === gradeLevelOptions[1]) {
        isGrade2 = true;
      } else if (handoverGradeLevel === null && grade === gradeLevelOptions[2]) {
        isGrade3 = true;
      }

      createClass(
        formattedClassTitle,
        grade,
        isGrade1,
        isGrade2,
        isGrade3,
        trialClass,
        false,
        finalClassOwner,
        setClass,
        selectedAcademicYearOption?.storage ||
          (handoverSelection ? selectedAcademicYear : null),
        handoverSelection?.progressionKey || null
      )
        .then(() => {
          if (!inviteColleagueClicked) {
            setTimeout(() => {
              props.router.navigate(handoverReturnDestination);
            }, 3000);
            updateUserActivity(teacherId, "class-created");
          } else {
            setTimeout(() => {
              props.router.navigate("/invitecolleagues", {
                state: {
                  rollNumber: { rollNumber },
                  teacherEmail: { teacherEmail },
                  countryId: { countryId },
                  schoolid: { schoolid },
                  name: { name },
                  teachers: { teachers },
                  teacherId: { teacherId },
                  fromCreateClass: fromCreateClass,
                },
              });
            }, 3000);
          }
        })
        .catch((error) => {
          console.error("Error creating class:", error);
        });
    }
    setShowModal(false);
  }

  function handleModalClose() {
    setShowModal(false);
  }

  function handleSelectOwner(teacherId) {
    setClassOwner(teacherId);
  }

  function handleInviteColleagueClick() {
    setIsInviteColleague(true);
    handleSelectOwner("");
    localStorage.removeItem("selectedTeacherId");

    setTimeout(() => {
      handleModalConfirm("", true);
    }, 0);

    toast.success("Class has been successfully created!");
  }

  const textBoxStyle = {
    border: "1px solid #ddd",
    borderRadius: "0.25rem",
    padding: "1rem",
    marginBottom: "1rem",
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
  };

  function handleFutureYearConfirm() {
    setShowFutureYearModal(false);
    setShowModal(true); // Proceed to owner confirmation modal
  }

  function handleFutureYearCancel() {
    setShowFutureYearModal(false);
    setSelectedAcademicYear(""); // Reset to empty (forces re-selection)
  }

  return (
    <div className="form-detail">
      <Form onSubmit={handleSubmit}>
        <div style={textBoxStyle}>
          <p>
            Please name classroom as it appears in your school MIS system e.g
            Aladdin / Arbor / i-SAMS - this will assist with reporting later.
          </p>
        </div>

        <Form.Group as={Row} className="mb-3" controlId="class-name">
          <Form.Label className="create-class-label" column sm="2">
            Class Name
          </Form.Label>
          <Col sm="10">
            <Form.Control
              placeholder="Class ABC..."
              size="md"
              type="text"
              value={`${classTitle}`}
              onChange={(e) => setClassTitle(e.target.value)}
              isInvalid={submitted && !!classTitleError}
            />
            {submitted && classTitleError && (
              <Form.Control.Feedback type="invalid">
                {classTitleError}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="grade">
          <Form.Label className="create-class-label" column sm="2">
            Class Level
          </Form.Label>
          <Col>
            <Form.Control
              as="select"
              size="md"
              value={selectedGradeLevel}
              onChange={(e) => setSelectedGradeLevel(e.target.value)}
              isInvalid={submitted && !!gradeError}
            >
              <option value="">Select Class Level...</option>
              {gradeLevelOptions.map((grade, index) => (
                <option key={index} value={grade}>
                  {grade}
                </option>
              ))}
            </Form.Control>
            {submitted && gradeError && (
              <Form.Control.Feedback type="invalid">
                {gradeError}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>

        {/* Academic Year Dropdown - Only show if enabled AND paid customer */}
        {academicYearConfig.enabled && (
          <Form.Group as={Row} className="mb-3" controlId="academic-year">
            <Form.Label className="create-class-label" column sm="2">
              Academic Year
            </Form.Label>
            <Col>
              <Form.Control
                as="select"
                size="md"
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value);
                  setAcademicYearError("");
                }}
                isInvalid={submitted && Boolean(academicYearError)}
              >
                <option value="">Select Academic Year...</option>
                {configuredAcademicYears.map((year) => (
                  <option key={year.storage} value={year.storage}>
                    {year.display}
                  </option>
                ))}
              </Form.Control>
              {submitted && academicYearError && (
                <Form.Control.Feedback type="invalid">
                  {academicYearError}
                </Form.Control.Feedback>
              )}
            </Col>
          </Form.Group>
        )}

        <div className="float-right">
          <SimpleNavigationButton
            path={handoverReturnDestination}
            label="Cancel"
            colour="white"
          />
          <button className="green-button" type="submit">
            Create
          </button>
        </div>
      </Form>

      <ConfirmClassOwnerModal
        show={showModal}
        onConfirm={handleModalConfirm}
        onClose={handleModalClose}
        teacherOptions={teacherOptions}
        onSelectOwner={handleSelectOwner}
        handleInviteColleagueClick={handleInviteColleagueClick}
      />

      <SeniorInfantPreCreateModal
        show={showSeniorInfantModal}
        onClose={handleSeniorInfantModalClose}
        onProceed={handleSeniorInfantModalProceed}
        availableClasses={availableConvertedClasses}
        fromGradeName={gradeLevelOptions[0]}
        toGradeName={gradeLevelOptions[1]}
      />

      <FutureYearConfirmationModal
        show={showFutureYearModal}
        onConfirm={handleFutureYearConfirm}
        onCancel={handleFutureYearCancel}
        gradeLevel={selectedGradeLevel}
        academicYear={selectedAcademicYear}
        transitionWarning={getTransitionWarning()}
      />
    </div>
  );
}

export default withRouter(CreateClassForm);


// import React, { useState, useEffect } from "react";
// import { withRouter } from "../utils/withRouter.js";
// import { useNavigate } from "react-router";
// import Row from "react-bootstrap/esm/Row";
// import Col from "react-bootstrap/esm/Col";
// import Form from "react-bootstrap/Form";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import {
//   createClass,
//   getCountryGrades,
//   getSchools,
//   getTeachers,
//   whoami,
//   updateUserActivity,
//   getAvailableTransitionedClasses,
// } from "../services/APIClient";
// import SimpleNavigationButton from "./SimpleNavigationButton";
// import ConfirmClassOwnerModal from "./ConfirmClassOwnerModal";
// import SeniorInfantPreCreateModal from "./SeniorInfantPreCreateModal";

// function CreateClassForm(props) {
//   const [selectedGradeLevel, setSelectedGradeLevel] = useState("");
//   const [classTitle, setClassTitle] = useState("");
//   const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
//   const [teacherOptions, setTeacherOptions] = useState([]);
//   const [classTitleError, setClassTitleError] = useState("");
//   const [gradeError, setGradeError] = useState("");
//   const [submitted, setSubmitted] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [classOwner, setClassOwner] = useState(null);

//   const [countryId, setCountryId] = useState(null);
//   const [isInviteColleague, setIsInviteColleague] = useState(false);

//   // State for Senior Infant conversion modal
//   const [showSeniorInfantModal, setShowSeniorInfantModal] = useState(false);
//   const [availableConvertedClasses, setAvailableConvertedClasses] = useState([]);

//   const teacherId = props.teacherid;
//   const schoolid = props.router.location.state.schoolid.schoolid;
//   let teacherRoles = props.teacherRoles;

//   const [rollNumber, setRollNumber] = useState("");

//   const [teacherEmail, setTeacherEmail] = useState("");
//   const [name, setName] = useState("");
//   const navigate = useNavigate();

//   let teachers = teacherOptions;
//   let fromCreateClass = true;


//   const allowedSchoolIdsForThirdGrade = [
//     "e0d40e40-0364-11ed-a237-4f910a76850a",
//     // "school-id-2",
//     // Add other allowed school IDs here
//   ];

//   // Check if running in staging environment
//   const isStaging =
//     window.location.href.includes("staging") ||
//     window.location.href.includes("localhost") ||
//     window.location.href.includes("alpaca-assessment-test");

//   // Helper function to calculate academic year
//   const calculateAcademicYear = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth() + 1;

//     let startYear;
//     let endYear;

//     if (month >= 8) {
//       startYear = year;
//       endYear = year + 1;
//     } else {
//       startYear = year - 1;
//       endYear = year;
//     }

//     const startYearShort = startYear.toString().slice(-2);
//     const endYearShort = endYear.toString().slice(-2);

//     return `${startYearShort}-${endYearShort}`;
//   };

//   useEffect(() => {
//     console.log(isInviteColleague);
//   }, [isInviteColleague]);

//   useEffect(() => {
//     whoami().then((response) => {
//       console.log(response, "WHOAMI");
//       setTeacherEmail(response.data.email);

//       setName(response.data.stakeholder.name);
//     });
//     getSchools().then((response) => {
//       console.log(response);
//       setCountryId(response.data.countryid);
//       setRollNumber(response.data.rollnumber);
//     });
//   }, []);

//   const defaultGradeLevel = () => {
//     const lowerCasedRoles = teacherRoles.map((role) => role.toLowerCase());

//     if (
//       lowerCasedRoles.some((role) => role.includes("set")) ||
//       lowerCasedRoles.some((role) => role.includes("senco"))
//     ) {
//       return "";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("junior infant"))) {
//       return "Junior Infants";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("senior infant"))) {
//       return "Senior Infants";
//     }

//     return "";
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const schoolsResponse = await getSchools();
//         const matchingSchool = schoolsResponse.data.find(
//           (school) => school.id === schoolid
//         );

//         if (matchingSchool) {
//           const countryid = matchingSchool.countryid;
//           setCountryId(countryid);
//           const gradesResponse = await getCountryGrades(countryid);
//           const { grade1, grade2, grade3 } = gradesResponse;
          
//           // Create base grade level options
//           let gradeOptions = [grade1, grade2];
          
//           // Add third grade option if in staging environment
//           if (isStaging && grade3) {
//             gradeOptions.push(grade3);
//           }
          
//           setGradeLevelOptions(gradeOptions);

//           const teachersResponse = await getTeachers(schoolid);
//           setTeacherOptions(teachersResponse);

//           const defaultGrade = defaultGradeLevel();
//           if (defaultGrade) {
//             setSelectedGradeLevel(defaultGrade);
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       }
//     };

//     fetchData();
//   }, [schoolid, teacherRoles, isStaging]);

//   // Function to fetch available transitioned Senior Infant classes
//   const fetchAvailableConvertedClasses = async () => {
//     try {
//       const currentAcademicYear = calculateAcademicYear(new Date());
//       const gradeLevel = 2; // Senior Infant grade level
      
//       // Fetch available transitioned classes for Senior Infant
//       const transitionedClassesResponse = await getAvailableTransitionedClasses(
//         schoolid,
//         gradeLevel,
//         currentAcademicYear
//       );

//       if (!transitionedClassesResponse.success) {
//         console.log('No transitioned classes available');
//         setAvailableConvertedClasses([]);
//         return;
//       }

//       // Transform the data to match the modal's expected format
//       const formattedClasses = transitionedClassesResponse.classes.map(classItem => ({
//         name: classItem.name,
//         hasOwner: false, // These are available classes, so they need owners
//         studentCount: classItem.studentCount || 0,
//         id: classItem.id
//       }));
      
//       setAvailableConvertedClasses(formattedClasses);
      
//       // Only show modal if there are classes that can be claimed
//       if (formattedClasses.length > 0) {
//         setShowSeniorInfantModal(true);
//       }
//     } catch (error) {
//       console.error("Error fetching available transitioned classes:", error);
//       setAvailableConvertedClasses([]);
//     }
//   };

//   // useEffect for Senior Infant selection
//   useEffect(() => {
//     // Check if Senior Infant is selected (still checking against index 1 for the original second grade)
//     if (selectedGradeLevel === gradeLevelOptions[1]) {
//       // Show conversion modal if there are classes to be claimed
//       fetchAvailableConvertedClasses();
//     }
//   }, [selectedGradeLevel, gradeLevelOptions]);

//   useEffect(() => {
//     if (submitted) {
//       const isClassTitleValid = classTitle.length > 0;
//       const isGradeValid = selectedGradeLevel.length > 0;

//       setClassTitleError(isClassTitleValid ? "" : "Please enter a class name.");
//       setGradeError(isGradeValid ? "" : "Please select a grade level.");
//     }
//   }, [classTitle, selectedGradeLevel, submitted]);

//   // Handle Senior Infant modal actions
//   const handleSeniorInfantModalClose = () => {
//     setShowSeniorInfantModal(false);
//     setSelectedGradeLevel(""); // Reset selection
//   };

//   const handleSeniorInfantModalProceed = () => {
//     setShowSeniorInfantModal(false);
//     // Continue with normal class creation flow
//     // The selectedGradeLevel is already set to Senior Infant
//   };

//   function handleSubmit(event) {
//     event.preventDefault();
//     setSubmitted(true);

//     const isClassTitleValid = classTitle.length > 0;
//     const isGradeValid = selectedGradeLevel.length > 0;

//     if (!isClassTitleValid || !isGradeValid) {
//       setClassTitleError(
//         !isClassTitleValid ? "Please enter a class name." : ""
//       );
//       setGradeError(!isGradeValid ? "Please select a grade level." : "");
//       return;
//     }

//     setShowModal(true);
//   }

//  function handleModalConfirm(
//   selectedTeacherId,
//   inviteColleagueClicked = isInviteColleague
// ) {
//   const matchingGrade = gradeLevelOptions.includes(selectedGradeLevel);
//   if (matchingGrade) {
//     const trialClass = false;
//     const setClass = false;
//     const grade = selectedGradeLevel;
//     const formattedClassTitle = `${classTitle}`;
//     const finalClassOwner = selectedTeacherId || teacherId;

//     let isGrade1 = false;
//     let isGrade2 = false;
//     let isGrade3 = false;

//     if (grade === gradeLevelOptions[0]) {
//       isGrade1 = true;
//     } else if (grade === gradeLevelOptions[1]) {
//       isGrade2 = true;
//     } else if (isStaging && grade === gradeLevelOptions[2]) {
//       isGrade3 = true;
//     }

//     createClass(
//       formattedClassTitle,
//       grade,
//       isGrade1,
//       isGrade2,
//       isGrade3,  
//       trialClass,
//       false,
//       finalClassOwner,
//       setClass
//     )
//       .then(() => {
//         if (!inviteColleagueClicked) {
//           setTimeout(() => {
//             props.router.navigate("/classadmindashboard");
//           }, 3000);
//           updateUserActivity(teacherId, "class-created");
//         } else {
//           setTimeout(() => {
//             props.router.navigate("/invitecolleagues", {
//               state: {
//                 rollNumber: { rollNumber },
//                 teacherEmail: { teacherEmail },
//                 countryId: { countryId },
//                 schoolid: { schoolid },
//                 name: { name },
//                 teachers: { teachers },
//                 teacherId: { teacherId },
//                 fromCreateClass: fromCreateClass,
//               },
//             });
//           }, 3000);
//         }
//       })
//       .catch((error) => {
//         console.error("Error creating class:", error);
//       });
//   }
//   setShowModal(false);
// }

//   function handleModalClose() {
//     setShowModal(false);
//   }

//   function handleSelectOwner(teacherId) {
//     setClassOwner(teacherId);
//   }

//   function handleInviteColleagueClick() {
//     setIsInviteColleague(true); // Set the invite colleague flag
//     handleSelectOwner(""); // Reset the owner
//     localStorage.removeItem("selectedTeacherId");

//     // Wait for the next render cycle where `isInviteColleague` will be true
//     setTimeout(() => {
//       handleModalConfirm("", true); // Pass true directly
//     }, 0); // This allows the state update to be processed before calling the confirm function

//     toast.success("Class has been successfully created!");
//   }

//   const textBoxStyle = {
//     border: "1px solid #ddd",
//     borderRadius: "0.25rem",
//     padding: "1rem",
//     marginBottom: "1rem",
//     fontSize: "18px",
//     fontWeight: "bold",
//     textAlign: "center",
//   };

//   return (
//     <div className="form-detail">
//       <Form onSubmit={handleSubmit}>
//         <div style={textBoxStyle}>
//           <p>
//             Please name classroom as it appears in your school MIS system e.g
//             Aladdin / Arbor / i-SAMS - this will assist with reporting later.
//           </p>
//         </div>

//         <Form.Group as={Row} className="mb-3" controlId="class-name">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Name
//           </Form.Label>
//           <Col sm="10">
//             <Form.Control
//               placeholder="Class ABC..."
//               size="md"
//               type="text"
//               value={`${classTitle}`}
//               onChange={(e) => setClassTitle(e.target.value)}
//               isInvalid={submitted && !!classTitleError}
//             />
//             {submitted && classTitleError && (
//               <Form.Control.Feedback type="invalid">
//                 {classTitleError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <Form.Group as={Row} className="mb-3" controlId="grade">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Level
//           </Form.Label>
//           <Col>
//             <Form.Control
//               as="select"
//               size="md"
//               value={selectedGradeLevel}
//               onChange={(e) => setSelectedGradeLevel(e.target.value)}
//               isInvalid={submitted && !!gradeError}
//             >
//               <option value="">Select Class Level...</option>
//               {gradeLevelOptions.map((grade, index) => {
//                 // Hide second grade option for specific country (existing logic)
//                 if (
//                   countryId === "74bb2897-dbbe-41e3-b547-caee866545cf" &&
//                   index === 1
//                 ) {
//                   return null;
//                 }
                
//                 // Only show third grade option (test grade) in staging
//                 if (index === 2 && !isStaging) {
//                   return null;
//                 }
                
//                 return (
//                   <option key={index} value={grade}>
//                     {grade}
//                   </option>
//                 );
//               })}
//             </Form.Control>
//             {submitted && gradeError && (
//               <Form.Control.Feedback type="invalid">
//                 {gradeError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <div className="float-right">
//           <SimpleNavigationButton
//             path="/classadmindashboard"
//             label="Cancel"
//             colour="white"
//           />
//           <button className="green-button" type="submit">
//             Create
//           </button>
//         </div>
//       </Form>

//       <ConfirmClassOwnerModal
//         show={showModal}
//         onConfirm={handleModalConfirm}
//         onClose={handleModalClose}
//         teacherOptions={teacherOptions}
//         onSelectOwner={handleSelectOwner}
//         handleInviteColleagueClick={handleInviteColleagueClick}
//       />

//       {/* Senior Infant Conversion Modal */}
//       <SeniorInfantPreCreateModal
//         show={showSeniorInfantModal}
//         onClose={handleSeniorInfantModalClose}
//         onProceed={handleSeniorInfantModalProceed}
//         availableClasses={availableConvertedClasses}
//       />
//     </div>
//   );
// }

// export default withRouter(CreateClassForm);

// import React, { useState, useEffect } from "react";
// import { withRouter } from "../utils/withRouter.js";
// import { useNavigate } from "react-router";
// import Row from "react-bootstrap/esm/Row";
// import Col from "react-bootstrap/esm/Col";
// import Form from "react-bootstrap/Form";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import {
//   createClass,
//   getCountryGrades,
//   getSchools,
//   getTeachers,
//   whoami,
//   updateUserActivity,
//   getAvailableTransitionedClasses,
// } from "../services/APIClient";
// import SimpleNavigationButton from "./SimpleNavigationButton";
// import ConfirmClassOwnerModal from "./ConfirmClassOwnerModal";
// import SeniorInfantPreCreateModal from "./SeniorInfantPreCreateModal";

// function CreateClassForm(props) {
//   const [selectedGradeLevel, setSelectedGradeLevel] = useState("");
//   const [classTitle, setClassTitle] = useState("");
//   const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
//   const [teacherOptions, setTeacherOptions] = useState([]);
//   const [classTitleError, setClassTitleError] = useState("");
//   const [gradeError, setGradeError] = useState("");
//   const [submitted, setSubmitted] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [classOwner, setClassOwner] = useState(null);

//   const [countryId, setCountryId] = useState(null);
//   const [isInviteColleague, setIsInviteColleague] = useState(false);

//   // State for Senior Infant conversion modal
//   const [showSeniorInfantModal, setShowSeniorInfantModal] = useState(false);
//   const [availableConvertedClasses, setAvailableConvertedClasses] = useState([]);

//   const teacherId = props.teacherid;
//   const schoolid = props.router.location.state.schoolid.schoolid;
//   let teacherRoles = props.teacherRoles;
  
//   // Updated props from parent component
//   const seniorInfantRestricted = props.seniorInfantRestricted;
//   const onShowRestrictionModal = props.onShowRestrictionModal;

//   const [rollNumber, setRollNumber] = useState("");

//   const [teacherEmail, setTeacherEmail] = useState("");
//   const [name, setName] = useState("");
//   const navigate = useNavigate();

//   let teachers = teacherOptions;
//   let fromCreateClass = true;

//   // Helper function to calculate academic year
//   const calculateAcademicYear = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth() + 1;

//     let startYear;
//     let endYear;

//     if (month >= 8) {
//       startYear = year;
//       endYear = year + 1;
//     } else {
//       startYear = year - 1;
//       endYear = year;
//     }

//     const startYearShort = startYear.toString().slice(-2);
//     const endYearShort = endYear.toString().slice(-2);

//     return `${startYearShort}-${endYearShort}`;
//   };

//   useEffect(() => {
//     console.log(isInviteColleague);
//   }, [isInviteColleague]);

//   useEffect(() => {
//     whoami().then((response) => {
//       console.log(response, "WHOAMI");
//       setTeacherEmail(response.data.email);

//       setName(response.data.stakeholder.name);
//     });
//     getSchools().then((response) => {
//       console.log(response);
//       setCountryId(response.data.countryid);
//       setRollNumber(response.data.rollnumber);
//     });
//   }, []);

//   const defaultGradeLevel = () => {
//     const lowerCasedRoles = teacherRoles.map((role) => role.toLowerCase());

//     if (
//       lowerCasedRoles.some((role) => role.includes("set")) ||
//       lowerCasedRoles.some((role) => role.includes("senco"))
//     ) {
//       return "";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("junior infant"))) {
//       return "Junior Infants";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("senior infant"))) {
//       return "Senior Infants";
//     }

//     return "";
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const schoolsResponse = await getSchools();
//         const matchingSchool = schoolsResponse.data.find(
//           (school) => school.id === schoolid
//         );

//         if (matchingSchool) {
//           const countryid = matchingSchool.countryid;
//           setCountryId(countryid);
//           const gradesResponse = await getCountryGrades(countryid);
//           const { grade1, grade2 } = gradesResponse;
//           setGradeLevelOptions([grade1, grade2]);

//           const teachersResponse = await getTeachers(schoolid);
//           setTeacherOptions(teachersResponse);

//           const defaultGrade = defaultGradeLevel();
//           if (defaultGrade) {
//             setSelectedGradeLevel(defaultGrade);
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       }
//     };

//     fetchData();
//   }, [schoolid, teacherRoles]);

//   // Function to fetch available transitioned Senior Infant classes
//   const fetchAvailableConvertedClasses = async () => {
//     try {
//       const currentAcademicYear = calculateAcademicYear(new Date());
//       const gradeLevel = 2; // Senior Infant grade level
      
//       // Fetch available transitioned classes for Senior Infant
//       const transitionedClassesResponse = await getAvailableTransitionedClasses(
//         schoolid,
//         gradeLevel,
//         currentAcademicYear
//       );

//       if (!transitionedClassesResponse.success) {
//         console.log('No transitioned classes available');
//         setAvailableConvertedClasses([]);
//         return;
//       }

//       // Transform the data to match the modal's expected format
//       const formattedClasses = transitionedClassesResponse.classes.map(classItem => ({
//         name: classItem.name,
//         hasOwner: false, // These are available classes, so they need owners
//         studentCount: classItem.studentCount || 0,
//         id: classItem.id
//       }));
      
//       setAvailableConvertedClasses(formattedClasses);
      
//       // Only show modal if there are classes that can be claimed
//       if (formattedClasses.length > 0) {
//         setShowSeniorInfantModal(true);
//       }
//     } catch (error) {
//       console.error("Error fetching available transitioned classes:", error);
//       setAvailableConvertedClasses([]);
//     }
//   };

//   // Updated useEffect for Senior Infant selection
//   useEffect(() => {
//     // Check if Senior Infant is selected
//     if (selectedGradeLevel === gradeLevelOptions[1]) {
//       if (seniorInfantRestricted && onShowRestrictionModal) {
//         // Show restriction modal if not allowed
//         onShowRestrictionModal();
//         setSelectedGradeLevel("");
//       } else if (!seniorInfantRestricted) {
//         // Show conversion modal if allowed and there are classes to be claimed
//         fetchAvailableConvertedClasses();
//       }
//     }
//   }, [selectedGradeLevel, gradeLevelOptions, seniorInfantRestricted, onShowRestrictionModal]);

//   useEffect(() => {
//     if (submitted) {
//       const isClassTitleValid = classTitle.length > 0;
//       const isGradeValid = selectedGradeLevel.length > 0;

//       setClassTitleError(isClassTitleValid ? "" : "Please enter a class name.");
//       setGradeError(isGradeValid ? "" : "Please select a grade level.");
//     }
//   }, [classTitle, selectedGradeLevel, submitted]);

//   // Handle Senior Infant modal actions
//   const handleSeniorInfantModalClose = () => {
//     setShowSeniorInfantModal(false);
//     setSelectedGradeLevel(""); // Reset selection
//   };

//   const handleSeniorInfantModalProceed = () => {
//     setShowSeniorInfantModal(false);
//     // Continue with normal class creation flow
//     // The selectedGradeLevel is already set to Senior Infant
//   };

//   function handleSubmit(event) {
//     event.preventDefault();
//     setSubmitted(true);

//     const isClassTitleValid = classTitle.length > 0;
//     const isGradeValid = selectedGradeLevel.length > 0;

//     // Additional check for Senior Infant restriction
//     if (selectedGradeLevel === gradeLevelOptions[1] && seniorInfantRestricted && onShowRestrictionModal) {
//       onShowRestrictionModal();
//       return;
//     }

//     if (!isClassTitleValid || !isGradeValid) {
//       setClassTitleError(
//         !isClassTitleValid ? "Please enter a class name." : ""
//       );
//       setGradeError(!isGradeValid ? "Please select a grade level." : "");
//       return;
//     }

//     setShowModal(true);
//   }

//   function handleModalConfirm(
//     selectedTeacherId,
//     inviteColleagueClicked = isInviteColleague
//   ) {
//     const matchingGrade = gradeLevelOptions.includes(selectedGradeLevel);
//     if (matchingGrade) {
//       const trialClass = false;
//       const setClass = false;
//       const grade = selectedGradeLevel;
//       const formattedClassTitle = `${classTitle}`;
//       const finalClassOwner = selectedTeacherId || teacherId;

//       createClass(
//         formattedClassTitle,
//         grade,
//         grade === gradeLevelOptions[0],
//         grade === gradeLevelOptions[1],
//         trialClass,
//         false,
//         finalClassOwner,
//         setClass
//       )
//         .then(() => {
//           if (!inviteColleagueClicked) {
//             setTimeout(() => {
//               props.router.navigate("/classadmindashboard");
//             }, 3000);
//             updateUserActivity(teacherId, "class-created"); // see useractivity column headings to know what to put in
//           } else {
//             setTimeout(() => {
//               props.router.navigate("/invitecolleagues", {
//                 state: {
//                   rollNumber: { rollNumber },
//                   teacherEmail: { teacherEmail },
//                   countryId: { countryId },
//                   schoolid: { schoolid },
//                   name: { name },
//                   teachers: { teachers },
//                   teacherId: { teacherId },
//                   fromCreateClass: fromCreateClass,
//                 },
//               });
//             }, 3000);
//           }
//         })
//         .catch((error) => {
//           console.error("Error creating class:", error);
//         });
//     }
//     setShowModal(false);
//   }

//   function handleModalClose() {
//     setShowModal(false);
//   }

//   function handleSelectOwner(teacherId) {
//     setClassOwner(teacherId);
//   }

//   function handleInviteColleagueClick() {
//     setIsInviteColleague(true); // Set the invite colleague flag
//     handleSelectOwner(""); // Reset the owner
//     localStorage.removeItem("selectedTeacherId");

//     // Wait for the next render cycle where `isInviteColleague` will be true
//     setTimeout(() => {
//       handleModalConfirm("", true); // Pass true directly
//     }, 0); // This allows the state update to be processed before calling the confirm function

//     toast.success("Class has been successfully created!");
//   }

//   const textBoxStyle = {
//     border: "1px solid #ddd",
//     borderRadius: "0.25rem",
//     padding: "1rem",
//     marginBottom: "1rem",
//     fontSize: "18px",
//     fontWeight: "bold",
//     textAlign: "center",
//   };

//   return (
//     <div className="form-detail">
//       <Form onSubmit={handleSubmit}>
//         <div style={textBoxStyle}>
//           <p>
//             Please name classroom as it appears in your school MIS system e.g
//             Aladdin / Arbor / i-SAMS - this will assist with reporting later.
//           </p>
//         </div>

//         <Form.Group as={Row} className="mb-3" controlId="class-name">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Name
//           </Form.Label>
//           <Col sm="10">
//             <Form.Control
//               placeholder="Class ABC..."
//               size="md"
//               type="text"
//               value={`${classTitle}`}
//               onChange={(e) => setClassTitle(e.target.value)}
//               isInvalid={submitted && !!classTitleError}
//             />
//             {submitted && classTitleError && (
//               <Form.Control.Feedback type="invalid">
//                 {classTitleError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <Form.Group as={Row} className="mb-3" controlId="grade">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Level
//           </Form.Label>
//           <Col>
//             <Form.Control
//               as="select"
//               size="md"
//               value={selectedGradeLevel}
//               onChange={(e) => setSelectedGradeLevel(e.target.value)}
//               isInvalid={submitted && !!gradeError}
//             >
//               <option value="">Select Class Level...</option>
//               {gradeLevelOptions.map((grade, index) => {
//                 if (
//                   countryId === "74bb2897-dbbe-41e3-b547-caee866545cf" &&
//                   index === 1
//                 ) {
//                   return null;
//                 }
//                 return (
//                   <option key={index} value={grade}>
//                     {grade}
//                   </option>
//                 );
//               })}
//             </Form.Control>
//             {submitted && gradeError && (
//               <Form.Control.Feedback type="invalid">
//                 {gradeError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <div className="float-right">
//           <SimpleNavigationButton
//             path="/classadmindashboard"
//             label="Cancel"
//             colour="white"
//           />
//           <button className="green-button" type="submit">
//             Create
//           </button>
//         </div>
//       </Form>

//       <ConfirmClassOwnerModal
//         show={showModal}
//         onConfirm={handleModalConfirm}
//         onClose={handleModalClose}
//         teacherOptions={teacherOptions}
//         onSelectOwner={handleSelectOwner}
//         handleInviteColleagueClick={handleInviteColleagueClick} // New prop to handle invite click
//       />

//       {/* Senior Infant Conversion Modal */}
//       <SeniorInfantPreCreateModal
//         show={showSeniorInfantModal}
//         onClose={handleSeniorInfantModalClose}
//         onProceed={handleSeniorInfantModalProceed}
//         availableClasses={availableConvertedClasses}
//       />
//     </div>
//   );
// }

// export default withRouter(CreateClassForm);


// import React, { useState, useEffect } from "react";
// import { withRouter } from "../utils/withRouter.js";
// import { useNavigate } from "react-router";
// import Row from "react-bootstrap/esm/Row";
// import Col from "react-bootstrap/esm/Col";
// import Form from "react-bootstrap/Form";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import {
//   createClass,
//   getCountryGrades,
//   getSchools,
//   getTeachers,
//   whoami,
//   updateUserActivity,
//   getAvailableTransitionedClasses,
// } from "../services/APIClient";
// import SimpleNavigationButton from "./SimpleNavigationButton";
// import ConfirmClassOwnerModal from "./ConfirmClassOwnerModal";
// import SeniorInfantPreCreateModal from "./SeniorInfantPreCreateModal";

// function CreateClassForm(props) {
//   const [selectedGradeLevel, setSelectedGradeLevel] = useState("");
//   const [classTitle, setClassTitle] = useState("");
//   const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
//   const [teacherOptions, setTeacherOptions] = useState([]);
//   const [classTitleError, setClassTitleError] = useState("");
//   const [gradeError, setGradeError] = useState("");
//   const [submitted, setSubmitted] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [classOwner, setClassOwner] = useState(null);

//   const [countryId, setCountryId] = useState(null);
//   const [isInviteColleague, setIsInviteColleague] = useState(false);

//   // State for Senior Infant conversion modal
//   const [showSeniorInfantModal, setShowSeniorInfantModal] = useState(false);
//   const [availableConvertedClasses, setAvailableConvertedClasses] = useState([]);

//   const teacherId = props.teacherid;
//   const schoolid = props.router.location.state.schoolid.schoolid;
//   let teacherRoles = props.teacherRoles;

//   const [rollNumber, setRollNumber] = useState("");

//   const [teacherEmail, setTeacherEmail] = useState("");
//   const [name, setName] = useState("");
//   const navigate = useNavigate();

//   let teachers = teacherOptions;
//   let fromCreateClass = true;


//   const allowedSchoolIdsForThirdGrade = [
//     "e0d40e40-0364-11ed-a237-4f910a76850a",
//     // "school-id-2",
//     // Add other allowed school IDs here
//   ];

//   // Check if running in staging environment
//   const isStaging =
//     window.location.href.includes("staging") ||
//     window.location.href.includes("localhost") ||
//     window.location.href.includes("alpaca-assessment-test");

//   // Helper function to calculate academic year
//   const calculateAcademicYear = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth() + 1;

//     let startYear;
//     let endYear;

//     if (month >= 8) {
//       startYear = year;
//       endYear = year + 1;
//     } else {
//       startYear = year - 1;
//       endYear = year;
//     }

//     const startYearShort = startYear.toString().slice(-2);
//     const endYearShort = endYear.toString().slice(-2);

//     return `${startYearShort}-${endYearShort}`;
//   };

//   useEffect(() => {
//     console.log(isInviteColleague);
//   }, [isInviteColleague]);

//   useEffect(() => {
//     whoami().then((response) => {
//       console.log(response, "WHOAMI");
//       setTeacherEmail(response.data.email);

//       setName(response.data.stakeholder.name);
//     });
//     getSchools().then((response) => {
//       console.log(response);
//       setCountryId(response.data.countryid);
//       setRollNumber(response.data.rollnumber);
//     });
//   }, []);

//   const defaultGradeLevel = () => {
//     const lowerCasedRoles = teacherRoles.map((role) => role.toLowerCase());

//     if (
//       lowerCasedRoles.some((role) => role.includes("set")) ||
//       lowerCasedRoles.some((role) => role.includes("senco"))
//     ) {
//       return "";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("junior infant"))) {
//       return "Junior Infants";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("senior infant"))) {
//       return "Senior Infants";
//     }

//     return "";
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const schoolsResponse = await getSchools();
//         const matchingSchool = schoolsResponse.data.find(
//           (school) => school.id === schoolid
//         );

//         if (matchingSchool) {
//           const countryid = matchingSchool.countryid;
//           setCountryId(countryid);
//           const gradesResponse = await getCountryGrades(countryid);
//           const { grade1, grade2, grade3 } = gradesResponse;
          
//           // Create base grade level options
//           let gradeOptions = [grade1, grade2];
          
//           // Add third grade option if in staging environment
//           if (isStaging && grade3) {
//             gradeOptions.push(grade3);
//           }
          
//           setGradeLevelOptions(gradeOptions);

//           const teachersResponse = await getTeachers(schoolid);
//           setTeacherOptions(teachersResponse);

//           const defaultGrade = defaultGradeLevel();
//           if (defaultGrade) {
//             setSelectedGradeLevel(defaultGrade);
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       }
//     };

//     fetchData();
//   }, [schoolid, teacherRoles, isStaging]);

//   // Function to fetch available transitioned Senior Infant classes
//   const fetchAvailableConvertedClasses = async () => {
//     try {
//       const currentAcademicYear = calculateAcademicYear(new Date());
//       const gradeLevel = 2; // Senior Infant grade level
      
//       // Fetch available transitioned classes for Senior Infant
//       const transitionedClassesResponse = await getAvailableTransitionedClasses(
//         schoolid,
//         gradeLevel,
//         currentAcademicYear
//       );

//       if (!transitionedClassesResponse.success) {
//         console.log('No transitioned classes available');
//         setAvailableConvertedClasses([]);
//         return;
//       }

//       // Transform the data to match the modal's expected format
//       const formattedClasses = transitionedClassesResponse.classes.map(classItem => ({
//         name: classItem.name,
//         hasOwner: false, // These are available classes, so they need owners
//         studentCount: classItem.studentCount || 0,
//         id: classItem.id
//       }));
      
//       setAvailableConvertedClasses(formattedClasses);
      
//       // Only show modal if there are classes that can be claimed
//       if (formattedClasses.length > 0) {
//         setShowSeniorInfantModal(true);
//       }
//     } catch (error) {
//       console.error("Error fetching available transitioned classes:", error);
//       setAvailableConvertedClasses([]);
//     }
//   };

//   // useEffect for Senior Infant selection
//   useEffect(() => {
//     // Check if Senior Infant is selected (still checking against index 1 for the original second grade)
//     if (selectedGradeLevel === gradeLevelOptions[1]) {
//       // Show conversion modal if there are classes to be claimed
//       fetchAvailableConvertedClasses();
//     }
//   }, [selectedGradeLevel, gradeLevelOptions]);

//   useEffect(() => {
//     if (submitted) {
//       const isClassTitleValid = classTitle.length > 0;
//       const isGradeValid = selectedGradeLevel.length > 0;

//       setClassTitleError(isClassTitleValid ? "" : "Please enter a class name.");
//       setGradeError(isGradeValid ? "" : "Please select a grade level.");
//     }
//   }, [classTitle, selectedGradeLevel, submitted]);

//   // Handle Senior Infant modal actions
//   const handleSeniorInfantModalClose = () => {
//     setShowSeniorInfantModal(false);
//     setSelectedGradeLevel(""); // Reset selection
//   };

//   const handleSeniorInfantModalProceed = () => {
//     setShowSeniorInfantModal(false);
//     // Continue with normal class creation flow
//     // The selectedGradeLevel is already set to Senior Infant
//   };

//   function handleSubmit(event) {
//     event.preventDefault();
//     setSubmitted(true);

//     const isClassTitleValid = classTitle.length > 0;
//     const isGradeValid = selectedGradeLevel.length > 0;

//     if (!isClassTitleValid || !isGradeValid) {
//       setClassTitleError(
//         !isClassTitleValid ? "Please enter a class name." : ""
//       );
//       setGradeError(!isGradeValid ? "Please select a grade level." : "");
//       return;
//     }

//     setShowModal(true);
//   }

//  function handleModalConfirm(
//   selectedTeacherId,
//   inviteColleagueClicked = isInviteColleague
// ) {
//   const matchingGrade = gradeLevelOptions.includes(selectedGradeLevel);
//   if (matchingGrade) {
//     const trialClass = false;
//     const setClass = false;
//     const grade = selectedGradeLevel;
//     const formattedClassTitle = `${classTitle}`;
//     const finalClassOwner = selectedTeacherId || teacherId;

//     let isGrade1 = false;
//     let isGrade2 = false;
//     let isGrade3 = false;

//     if (grade === gradeLevelOptions[0]) {
//       isGrade1 = true;
//     } else if (grade === gradeLevelOptions[1]) {
//       isGrade2 = true;
//     } else if (isStaging && grade === gradeLevelOptions[2]) {
//       isGrade3 = true;
//     }

//     createClass(
//       formattedClassTitle,
//       grade,
//       isGrade1,
//       isGrade2,
//       isGrade3,  
//       trialClass,
//       false,
//       finalClassOwner,
//       setClass
//     )
//       .then(() => {
//         if (!inviteColleagueClicked) {
//           setTimeout(() => {
//             props.router.navigate("/classadmindashboard");
//           }, 3000);
//           updateUserActivity(teacherId, "class-created");
//         } else {
//           setTimeout(() => {
//             props.router.navigate("/invitecolleagues", {
//               state: {
//                 rollNumber: { rollNumber },
//                 teacherEmail: { teacherEmail },
//                 countryId: { countryId },
//                 schoolid: { schoolid },
//                 name: { name },
//                 teachers: { teachers },
//                 teacherId: { teacherId },
//                 fromCreateClass: fromCreateClass,
//               },
//             });
//           }, 3000);
//         }
//       })
//       .catch((error) => {
//         console.error("Error creating class:", error);
//       });
//   }
//   setShowModal(false);
// }

//   function handleModalClose() {
//     setShowModal(false);
//   }

//   function handleSelectOwner(teacherId) {
//     setClassOwner(teacherId);
//   }

//   function handleInviteColleagueClick() {
//     setIsInviteColleague(true); // Set the invite colleague flag
//     handleSelectOwner(""); // Reset the owner
//     localStorage.removeItem("selectedTeacherId");

//     // Wait for the next render cycle where `isInviteColleague` will be true
//     setTimeout(() => {
//       handleModalConfirm("", true); // Pass true directly
//     }, 0); // This allows the state update to be processed before calling the confirm function

//     toast.success("Class has been successfully created!");
//   }

//   const textBoxStyle = {
//     border: "1px solid #ddd",
//     borderRadius: "0.25rem",
//     padding: "1rem",
//     marginBottom: "1rem",
//     fontSize: "18px",
//     fontWeight: "bold",
//     textAlign: "center",
//   };

//   return (
//     <div className="form-detail">
//       <Form onSubmit={handleSubmit}>
//         <div style={textBoxStyle}>
//           <p>
//             Please name classroom as it appears in your school MIS system e.g
//             Aladdin / Arbor / i-SAMS - this will assist with reporting later.
//           </p>
//         </div>

//         <Form.Group as={Row} className="mb-3" controlId="class-name">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Name
//           </Form.Label>
//           <Col sm="10">
//             <Form.Control
//               placeholder="Class ABC..."
//               size="md"
//               type="text"
//               value={`${classTitle}`}
//               onChange={(e) => setClassTitle(e.target.value)}
//               isInvalid={submitted && !!classTitleError}
//             />
//             {submitted && classTitleError && (
//               <Form.Control.Feedback type="invalid">
//                 {classTitleError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <Form.Group as={Row} className="mb-3" controlId="grade">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Level
//           </Form.Label>
//           <Col>
//             <Form.Control
//               as="select"
//               size="md"
//               value={selectedGradeLevel}
//               onChange={(e) => setSelectedGradeLevel(e.target.value)}
//               isInvalid={submitted && !!gradeError}
//             >
//               <option value="">Select Class Level...</option>
//               {gradeLevelOptions.map((grade, index) => {
//                 // Hide second grade option for specific country (existing logic)
//                 if (
//                   countryId === "74bb2897-dbbe-41e3-b547-caee866545cf" &&
//                   index === 1
//                 ) {
//                   return null;
//                 }
                
//                 // Only show third grade option (test grade) in staging
//                 if (index === 2 && !isStaging) {
//                   return null;
//                 }
                
//                 return (
//                   <option key={index} value={grade}>
//                     {grade}
//                   </option>
//                 );
//               })}
//             </Form.Control>
//             {submitted && gradeError && (
//               <Form.Control.Feedback type="invalid">
//                 {gradeError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <div className="float-right">
//           <SimpleNavigationButton
//             path="/classadmindashboard"
//             label="Cancel"
//             colour="white"
//           />
//           <button className="green-button" type="submit">
//             Create
//           </button>
//         </div>
//       </Form>

//       <ConfirmClassOwnerModal
//         show={showModal}
//         onConfirm={handleModalConfirm}
//         onClose={handleModalClose}
//         teacherOptions={teacherOptions}
//         onSelectOwner={handleSelectOwner}
//         handleInviteColleagueClick={handleInviteColleagueClick}
//       />

//       {/* Senior Infant Conversion Modal */}
//       <SeniorInfantPreCreateModal
//         show={showSeniorInfantModal}
//         onClose={handleSeniorInfantModalClose}
//         onProceed={handleSeniorInfantModalProceed}
//         availableClasses={availableConvertedClasses}
//       />
//     </div>
//   );
// }

// export default withRouter(CreateClassForm);

// import React, { useState, useEffect } from "react";
// import { withRouter } from "../utils/withRouter.js";
// import { useNavigate } from "react-router";
// import Row from "react-bootstrap/esm/Row";
// import Col from "react-bootstrap/esm/Col";
// import Form from "react-bootstrap/Form";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import {
//   createClass,
//   getCountryGrades,
//   getSchools,
//   getTeachers,
//   whoami,
//   updateUserActivity,
//   getAvailableTransitionedClasses,
// } from "../services/APIClient";
// import SimpleNavigationButton from "./SimpleNavigationButton";
// import ConfirmClassOwnerModal from "./ConfirmClassOwnerModal";
// import SeniorInfantPreCreateModal from "./SeniorInfantPreCreateModal";

// function CreateClassForm(props) {
//   const [selectedGradeLevel, setSelectedGradeLevel] = useState("");
//   const [classTitle, setClassTitle] = useState("");
//   const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
//   const [teacherOptions, setTeacherOptions] = useState([]);
//   const [classTitleError, setClassTitleError] = useState("");
//   const [gradeError, setGradeError] = useState("");
//   const [submitted, setSubmitted] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [classOwner, setClassOwner] = useState(null);

//   const [countryId, setCountryId] = useState(null);
//   const [isInviteColleague, setIsInviteColleague] = useState(false);

//   // State for Senior Infant conversion modal
//   const [showSeniorInfantModal, setShowSeniorInfantModal] = useState(false);
//   const [availableConvertedClasses, setAvailableConvertedClasses] = useState([]);

//   const teacherId = props.teacherid;
//   const schoolid = props.router.location.state.schoolid.schoolid;
//   let teacherRoles = props.teacherRoles;
  
//   // Updated props from parent component
//   const seniorInfantRestricted = props.seniorInfantRestricted;
//   const onShowRestrictionModal = props.onShowRestrictionModal;

//   const [rollNumber, setRollNumber] = useState("");

//   const [teacherEmail, setTeacherEmail] = useState("");
//   const [name, setName] = useState("");
//   const navigate = useNavigate();

//   let teachers = teacherOptions;
//   let fromCreateClass = true;

//   // Helper function to calculate academic year
//   const calculateAcademicYear = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth() + 1;

//     let startYear;
//     let endYear;

//     if (month >= 8) {
//       startYear = year;
//       endYear = year + 1;
//     } else {
//       startYear = year - 1;
//       endYear = year;
//     }

//     const startYearShort = startYear.toString().slice(-2);
//     const endYearShort = endYear.toString().slice(-2);

//     return `${startYearShort}-${endYearShort}`;
//   };

//   useEffect(() => {
//     console.log(isInviteColleague);
//   }, [isInviteColleague]);

//   useEffect(() => {
//     whoami().then((response) => {
//       console.log(response, "WHOAMI");
//       setTeacherEmail(response.data.email);

//       setName(response.data.stakeholder.name);
//     });
//     getSchools().then((response) => {
//       console.log(response);
//       setCountryId(response.data.countryid);
//       setRollNumber(response.data.rollnumber);
//     });
//   }, []);

//   const defaultGradeLevel = () => {
//     const lowerCasedRoles = teacherRoles.map((role) => role.toLowerCase());

//     if (
//       lowerCasedRoles.some((role) => role.includes("set")) ||
//       lowerCasedRoles.some((role) => role.includes("senco"))
//     ) {
//       return "";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("junior infant"))) {
//       return "Junior Infants";
//     }

//     if (lowerCasedRoles.some((role) => role.includes("senior infant"))) {
//       return "Senior Infants";
//     }

//     return "";
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const schoolsResponse = await getSchools();
//         const matchingSchool = schoolsResponse.data.find(
//           (school) => school.id === schoolid
//         );

//         if (matchingSchool) {
//           const countryid = matchingSchool.countryid;
//           setCountryId(countryid);
//           const gradesResponse = await getCountryGrades(countryid);
//           const { grade1, grade2 } = gradesResponse;
//           setGradeLevelOptions([grade1, grade2]);

//           const teachersResponse = await getTeachers(schoolid);
//           setTeacherOptions(teachersResponse);

//           const defaultGrade = defaultGradeLevel();
//           if (defaultGrade) {
//             setSelectedGradeLevel(defaultGrade);
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       }
//     };

//     fetchData();
//   }, [schoolid, teacherRoles]);

//   // Function to fetch available transitioned Senior Infant classes
//   const fetchAvailableConvertedClasses = async () => {
//     try {
//       const currentAcademicYear = calculateAcademicYear(new Date());
//       const gradeLevel = 2; // Senior Infant grade level
      
//       // Fetch available transitioned classes for Senior Infant
//       const transitionedClassesResponse = await getAvailableTransitionedClasses(
//         schoolid,
//         gradeLevel,
//         currentAcademicYear
//       );

//       if (!transitionedClassesResponse.success) {
//         console.log('No transitioned classes available');
//         setAvailableConvertedClasses([]);
//         return;
//       }

//       // Transform the data to match the modal's expected format
//       const formattedClasses = transitionedClassesResponse.classes.map(classItem => ({
//         name: classItem.name,
//         hasOwner: false, // These are available classes, so they need owners
//         studentCount: classItem.studentCount || 0,
//         id: classItem.id
//       }));
      
//       setAvailableConvertedClasses(formattedClasses);
      
//       // Only show modal if there are classes that can be claimed
//       if (formattedClasses.length > 0) {
//         setShowSeniorInfantModal(true);
//       }
//     } catch (error) {
//       console.error("Error fetching available transitioned classes:", error);
//       setAvailableConvertedClasses([]);
//     }
//   };

//   // Updated useEffect for Senior Infant selection
//   useEffect(() => {
//     // Check if Senior Infant is selected
//     if (selectedGradeLevel === gradeLevelOptions[1]) {
//       if (seniorInfantRestricted && onShowRestrictionModal) {
//         // Show restriction modal if not allowed
//         onShowRestrictionModal();
//         setSelectedGradeLevel("");
//       } else if (!seniorInfantRestricted) {
//         // Show conversion modal if allowed and there are classes to be claimed
//         fetchAvailableConvertedClasses();
//       }
//     }
//   }, [selectedGradeLevel, gradeLevelOptions, seniorInfantRestricted, onShowRestrictionModal]);

//   useEffect(() => {
//     if (submitted) {
//       const isClassTitleValid = classTitle.length > 0;
//       const isGradeValid = selectedGradeLevel.length > 0;

//       setClassTitleError(isClassTitleValid ? "" : "Please enter a class name.");
//       setGradeError(isGradeValid ? "" : "Please select a grade level.");
//     }
//   }, [classTitle, selectedGradeLevel, submitted]);

//   // Handle Senior Infant modal actions
//   const handleSeniorInfantModalClose = () => {
//     setShowSeniorInfantModal(false);
//     setSelectedGradeLevel(""); // Reset selection
//   };

//   const handleSeniorInfantModalProceed = () => {
//     setShowSeniorInfantModal(false);
//     // Continue with normal class creation flow
//     // The selectedGradeLevel is already set to Senior Infant
//   };

//   function handleSubmit(event) {
//     event.preventDefault();
//     setSubmitted(true);

//     const isClassTitleValid = classTitle.length > 0;
//     const isGradeValid = selectedGradeLevel.length > 0;

//     // Additional check for Senior Infant restriction
//     if (selectedGradeLevel === gradeLevelOptions[1] && seniorInfantRestricted && onShowRestrictionModal) {
//       onShowRestrictionModal();
//       return;
//     }

//     if (!isClassTitleValid || !isGradeValid) {
//       setClassTitleError(
//         !isClassTitleValid ? "Please enter a class name." : ""
//       );
//       setGradeError(!isGradeValid ? "Please select a grade level." : "");
//       return;
//     }

//     setShowModal(true);
//   }

//   function handleModalConfirm(
//     selectedTeacherId,
//     inviteColleagueClicked = isInviteColleague
//   ) {
//     const matchingGrade = gradeLevelOptions.includes(selectedGradeLevel);
//     if (matchingGrade) {
//       const trialClass = false;
//       const setClass = false;
//       const grade = selectedGradeLevel;
//       const formattedClassTitle = `${classTitle}`;
//       const finalClassOwner = selectedTeacherId || teacherId;

//       createClass(
//         formattedClassTitle,
//         grade,
//         grade === gradeLevelOptions[0],
//         grade === gradeLevelOptions[1],
//         trialClass,
//         false,
//         finalClassOwner,
//         setClass
//       )
//         .then(() => {
//           if (!inviteColleagueClicked) {
//             setTimeout(() => {
//               props.router.navigate("/classadmindashboard");
//             }, 3000);
//             updateUserActivity(teacherId, "class-created"); // see useractivity column headings to know what to put in
//           } else {
//             setTimeout(() => {
//               props.router.navigate("/invitecolleagues", {
//                 state: {
//                   rollNumber: { rollNumber },
//                   teacherEmail: { teacherEmail },
//                   countryId: { countryId },
//                   schoolid: { schoolid },
//                   name: { name },
//                   teachers: { teachers },
//                   teacherId: { teacherId },
//                   fromCreateClass: fromCreateClass,
//                 },
//               });
//             }, 3000);
//           }
//         })
//         .catch((error) => {
//           console.error("Error creating class:", error);
//         });
//     }
//     setShowModal(false);
//   }

//   function handleModalClose() {
//     setShowModal(false);
//   }

//   function handleSelectOwner(teacherId) {
//     setClassOwner(teacherId);
//   }

//   function handleInviteColleagueClick() {
//     setIsInviteColleague(true); // Set the invite colleague flag
//     handleSelectOwner(""); // Reset the owner
//     localStorage.removeItem("selectedTeacherId");

//     // Wait for the next render cycle where `isInviteColleague` will be true
//     setTimeout(() => {
//       handleModalConfirm("", true); // Pass true directly
//     }, 0); // This allows the state update to be processed before calling the confirm function

//     toast.success("Class has been successfully created!");
//   }

//   const textBoxStyle = {
//     border: "1px solid #ddd",
//     borderRadius: "0.25rem",
//     padding: "1rem",
//     marginBottom: "1rem",
//     fontSize: "18px",
//     fontWeight: "bold",
//     textAlign: "center",
//   };

//   return (
//     <div className="form-detail">
//       <Form onSubmit={handleSubmit}>
//         <div style={textBoxStyle}>
//           <p>
//             Please name classroom as it appears in your school MIS system e.g
//             Aladdin / Arbor / i-SAMS - this will assist with reporting later.
//           </p>
//         </div>

//         <Form.Group as={Row} className="mb-3" controlId="class-name">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Name
//           </Form.Label>
//           <Col sm="10">
//             <Form.Control
//               placeholder="Class ABC..."
//               size="md"
//               type="text"
//               value={`${classTitle}`}
//               onChange={(e) => setClassTitle(e.target.value)}
//               isInvalid={submitted && !!classTitleError}
//             />
//             {submitted && classTitleError && (
//               <Form.Control.Feedback type="invalid">
//                 {classTitleError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <Form.Group as={Row} className="mb-3" controlId="grade">
//           <Form.Label className="create-class-label" column sm="2">
//             Class Level
//           </Form.Label>
//           <Col>
//             <Form.Control
//               as="select"
//               size="md"
//               value={selectedGradeLevel}
//               onChange={(e) => setSelectedGradeLevel(e.target.value)}
//               isInvalid={submitted && !!gradeError}
//             >
//               <option value="">Select Class Level...</option>
//               {gradeLevelOptions.map((grade, index) => {
//                 if (
//                   countryId === "74bb2897-dbbe-41e3-b547-caee866545cf" &&
//                   index === 1
//                 ) {
//                   return null;
//                 }
//                 return (
//                   <option key={index} value={grade}>
//                     {grade}
//                   </option>
//                 );
//               })}
//             </Form.Control>
//             {submitted && gradeError && (
//               <Form.Control.Feedback type="invalid">
//                 {gradeError}
//               </Form.Control.Feedback>
//             )}
//           </Col>
//         </Form.Group>

//         <div className="float-right">
//           <SimpleNavigationButton
//             path="/classadmindashboard"
//             label="Cancel"
//             colour="white"
//           />
//           <button className="green-button" type="submit">
//             Create
//           </button>
//         </div>
//       </Form>

//       <ConfirmClassOwnerModal
//         show={showModal}
//         onConfirm={handleModalConfirm}
//         onClose={handleModalClose}
//         teacherOptions={teacherOptions}
//         onSelectOwner={handleSelectOwner}
//         handleInviteColleagueClick={handleInviteColleagueClick} // New prop to handle invite click
//       />

//       {/* Senior Infant Conversion Modal */}
//       <SeniorInfantPreCreateModal
//         show={showSeniorInfantModal}
//         onClose={handleSeniorInfantModalClose}
//         onProceed={handleSeniorInfantModalProceed}
//         availableClasses={availableConvertedClasses}
//       />
//     </div>
//   );
// }

// export default withRouter(CreateClassForm);
